import React from 'react';
import { ConfigProvider, Tag, theme } from 'antd';

import { CustomTooltip } from '~components/Tooltip';

import { httpStatusTagColor, NO_VALUE, NOT_PROXIED_TOOLTIP } from './PagesFormatting';

const HTTP_STATUS_TOOLTIPS: Record<string, string | undefined> = {
  '200':
    'Prerender last served this URL to Googlebot with a 200 OK. Delivery worked, but Google can still leave the page out of the index.',
  '301':
    'Prerender last served this URL to Googlebot as a permanent redirect, so Googlebot went to another URL instead of this page. That is expected on retired URLs, but worth checking on any URL that should still serve its own content.',
  '404':
    'Prerender last served this URL to Googlebot as a 404, the not-found status. Open the URL to check whether the page should still be live.',
  '403':
    'Prerender last served this URL to Googlebot as a 403, meaning access was refused. Googlebot cannot read a page it is not allowed to fetch, so nothing here can be indexed.',
  '503':
    'Prerender last served this URL to Googlebot as a 503, meaning your origin was unavailable. Check the page yourself, because Googlebot retries but will not index a page it cannot load.',
};

// Prototype is 11px; antd has no token below fontSizeSM. Scoping Tag.fontSizeSM resizes tags without a hardcoded style.
const STATUS_TAG_FONT_SIZE = 11; // FIXME: no exact token

// Don't say "last served to Googlebot" — the crawl cell still shows never-crawled.
function formatNotYetCrawledTooltip(status: number): string {
  return `Prerender has this URL cached with a ${status} response, but Googlebot has not requested it in this period. The cached response is what a crawler would get.`;
}

export type PagesHttpStatusValueProps = {
  statusCode: number | null;
  lastCrawledAt: string | null;
};

/**
 * HTTP Status value shared by the Pages table column and the inspect drawer,
 * so the tag, colour and tooltip copy can't drift between the two surfaces.
 */
function PagesHttpStatusValue({ statusCode, lastCrawledAt }: PagesHttpStatusValueProps) {
  const { token } = theme.useToken();

  if (statusCode === null) {
    return (
      <CustomTooltip placement="top" message={NOT_PROXIED_TOOLTIP}>
        <span style={{ color: token.colorTextTertiary }}>{NO_VALUE}</span>
      </CustomTooltip>
    );
  }

  const everCrawled = lastCrawledAt !== null;
  const tooltip = everCrawled ? HTTP_STATUS_TOOLTIPS[String(statusCode)] : formatNotYetCrawledTooltip(statusCode);
  const tag = (
    <ConfigProvider theme={{ components: { Tag: { fontSizeSM: STATUS_TAG_FONT_SIZE } } }}>
      <Tag color={httpStatusTagColor(statusCode)} style={{ marginInlineEnd: 0 }}>
        {statusCode}
      </Tag>
    </ConfigProvider>
  );

  return tooltip ? (
    <CustomTooltip placement="top" message={tooltip}>
      {tag}
    </CustomTooltip>
  ) : (
    tag
  );
}

export default PagesHttpStatusValue;
